import { Badge as MantineBadge, BadgeProps as MantineBadgeProps } from '@mantine/core';
import { cva, VariantProps } from 'class-variance-authority';
import { FC } from 'react';
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle, type IconProps } from '@tabler/icons-react';

import styles from './Tag.module.css';
import { joinClassNames } from '../../utils/joinClassNames';

const tagTypeVariants = cva(styles.base, {
    variants: {
        type: {
            neutral: styles.neutral,
            info: styles.info,
            positive: styles.positive,
            caution: styles.caution,
            negative: styles.negative,
            minimal: styles.minimal,
            prominent: styles.prominent,
            disabled: styles.disabled,
        },
    },
    defaultVariants: {
        type: 'neutral',
    },
});

const tagIconVariants: Record<NonNullable<VariantProps<typeof tagTypeVariants>['type']>, FC<IconProps>> = {
    neutral: IconInfoCircle,
    info: IconInfoCircle,
    positive: IconCircleCheck,
    caution: IconAlertTriangle,
    negative: IconInfoCircle,
    minimal: IconInfoCircle,
    prominent: IconInfoCircle,
    disabled: IconInfoCircle,
};

export type TagProps = Omit<MantineBadgeProps, 'radius'> &
    VariantProps<typeof tagTypeVariants> & {
        /**
         * The text usually displayed in a 'tooltip' popup when the mouse is over the node.
         *
         * {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/title Documentation}
         */
        title?: string;
        /**
         * When specified, do not truncate the content of the Tag. This can result in the Tag itself
         * overflowing the bounds of the parent container.
         *
         * @type {true | undefined}
         */
        showOverflow?: true;
    };

export const Tag: FC<TagProps> = ({ children, type, className, ...props }) => {
    const Icon = tagIconVariants[type ?? 'neutral'];

    return (
        <MantineBadge
            className={joinClassNames(
                tagTypeVariants({ type }),
                props.showOverflow && styles.showOverflow,
                className
            )}
            leftSection={<Icon className={styles.icon} />}
            {...props}
        >
            {children}
        </MantineBadge>
    );
};
